import { exists } from '@jkhy/frontend-utils/functions';
import { NavigationsClient, SsoClient } from '@treasury/api/channel';
import { getChannelAPIPath } from '@treasury/core/http';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { WindowService } from '@treasury/utils/services';
import { type DiContainer } from './electronicDocumentsSrvc';

interface SsoResponseMarkup {
    formMarkup: string;
    browserTarget: 'iframe' | 'newtab';
}

interface SsoResponseNewTab extends SsoResponseMarkup {
    browserTarget: 'newtab';
}

interface SsoResponseIframe extends SsoResponseMarkup {
    browserTarget: 'iframe';
}
interface SsoResponseRedirect {
    redirectTo: string;
}

export type SsoResponse = SsoResponseNewTab | SsoResponseIframe | SsoResponseRedirect;

/**
 * Service for retrieving navigation menu items and handling the SSO processes
 * associated with them.
 *
 * Not to be confused with the routing `NavigationService` in `@treasury/core/navigation`.
 */
export function NavigationsServiceFactory(
    $resource: ng.resource.IResourceService,
    TmDi: DiContainer
) {
    return {
        getNavigation,
        startSso,
        completeSso,
        userActivityAudit,
    };

    async function getNavigation() {
        const ffService = await TmDi.getAsync(FeatureFlagService);
        const navClient = await TmDi.getAsync(NavigationsClient);
        const ssoNavigationsEnabled = await ffService.isEnabled(Feature.SsoNavigations);

        if (ssoNavigationsEnabled) {
            return (await navClient.navigationsGetNavigationsWithSso()).data;
        } else {
            return (await navClient.navigationsGet()).data;
        }
    }

    /**
     * Begin the SSO process for a given provider ID.
     *
     * Upon completing the request, one of 3 things will occur to continue the SSO process:
     * 1. A new browser tab will open.
     * 2. An iframe will be created and placed into the provided element.
     * 3. The user will be redirected to the SSO (deprecated).
     */
    async function startSso(ssoUniqueId: string): Promise<SsoResponse> {
        const ssoClient = await TmDi.getAsync(SsoClient);
        const response = (
            await ssoClient.initiateSsoPost({
                ssoUniqueId,
            })
        ).data;
        const { formMarkup, browserTarget, redirectTo } = response;

        if (exists(redirectTo)) {
            return { redirectTo };
        } else if (exists(formMarkup)) {
            return { formMarkup, browserTarget: browserTarget || 'newtab' } as SsoResponseMarkup;
        } else {
            throw new Error(`Bad SSO response for: ${ssoUniqueId}`);
        }
    }

    async function completeSso(
        response: SsoResponseIframe,
        iframe: HTMLIFrameElement
    ): Promise<void>;
    async function completeSso(response: SsoResponseNewTab | SsoResponseRedirect): Promise<void>;
    /**
     * Given an SSO response, complete the SSO process by either
     * redirecting to the provided URI or rendering the provided markup.
     *
     * @param iframe The <iframe> to place the markup from the SSO into, if configured for it.
     */
    async function completeSso(response: SsoResponse, iframe?: HTMLIFrameElement): Promise<void> {
        const window = await TmDi.getAsync(WindowService);

        if ('redirectTo' in response) {
            window.open(response.redirectTo, '_blank');
        } else if ('formMarkup' in response) {
            const { formMarkup, browserTarget } = response;
            switch (browserTarget) {
                case 'iframe': {
                    if (!iframe) {
                        throw new Error(
                            'An iframe element must be provided to render SSO content.'
                        );
                    }
                    const iframeDoc = iframe.contentWindow?.document;
                    if (!iframeDoc) {
                        throw new Error('Could not create iframe for SSO content.');
                    }

                    iframeDoc.open();
                    iframeDoc.write(formMarkup);
                    iframeDoc.close();
                    break;
                }
                case 'newtab':
                default:
                    window.openWithHtml(formMarkup);
                    break;
            }
        } else {
            throw new Error('Bad SSO response.');
        }
    }

    function userActivityAudit(navigation: string) {
        const request = $resource(
            `${getChannelAPIPath()}navigations/auditUserActivity/${navigation}`
        );
        return request.save().$promise;
    }
}

NavigationsServiceFactory.$inject = ['$resource', 'TmDi'];

export type NgNavigationsService = ReturnType<typeof NavigationsServiceFactory>;

export function isSsoIframeResponse(ssoResponse: SsoResponse): ssoResponse is SsoResponseIframe {
    return 'browserTarget' in ssoResponse && ssoResponse.browserTarget === 'iframe';
}
